import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import Button from "@atlaskit/button";
import ChevronDownIcon from "@atlaskit/icon/utility/chevron-down";
import ChevronUpIcon from "@atlaskit/icon/utility/chevron-up";

import { templateDescriptionRelayFragment$key } from "@/__generated__/templateDescriptionRelayFragment.graphql";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";

interface TemplateDescriptionProps {
    template: templateDescriptionRelayFragment$key;
    isCfind3646Treatment: boolean;
}

const truncateTwoLines = css({
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
});

const buttonStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    marginBlockStart: 8
});

const buttonTextStyle = css({
    fontWeight: 400
});

const TemplateDescriptionRelayFragment = graphql`
    fragment templateDescriptionRelayFragment on ContentPlatformTemplate {
        aboutThisTemplate
    }
`;

export const TemplateDescription = ({
    template,
    isCfind3646Treatment: isCfind3646Treatment
}: TemplateDescriptionProps) => {
    const { aboutThisTemplate } = useFragment(
        TemplateDescriptionRelayFragment,
        template
    );

    const intl = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const seeMore = intl.formatMessage({
        id: "ConfluenceTemplateDetailsPage.templatesSection.seeMore",
        defaultMessage: "See more"
    });
    const seeLess = intl.formatMessage({
        id: "ConfluenceTemplateDetailsPage.templatesSection.seeLess",
        defaultMessage: "See less"
    });

    useEffect(() => {
        if (descriptionRef.current) {
            const descriptionElement = descriptionRef.current;
            setIsTruncated(
                descriptionElement.scrollHeight >
                    descriptionElement.clientHeight
            );
        }
    }, [aboutThisTemplate]);

    if (!aboutThisTemplate) {
        return null;
    }

    const renderDescription = () => {
        if (isCfind3646Treatment) {
            return (
                <>
                    <div
                        ref={descriptionRef}
                        css={[!isExpanded && truncateTwoLines]}
                    >
                        <SanitizedRichText innerHTML={aboutThisTemplate} />
                    </div>
                    {isTruncated && (
                        <div css={buttonStyle}>
                            <Button
                                appearance="link"
                                onClick={() => setIsExpanded((prev) => !prev)}
                                iconAfter={
                                    isExpanded ? (
                                        <ChevronUpIcon label="Collapse" />
                                    ) : (
                                        <ChevronDownIcon label="Expand" />
                                    )
                                }
                                aria-expanded={isExpanded}
                            >
                                <span css={buttonTextStyle}>
                                    {isExpanded ? seeLess : seeMore}
                                </span>
                            </Button>
                        </div>
                    )}
                </>
            );
        } else return <SanitizedRichText innerHTML={aboutThisTemplate} />;
    };

    return (
        <section data-testid="confluence-template-details-template-description">
            {renderDescription()}
        </section>
    );
};
